import { cn } from "@/lib/utils";
import { optimizeImage } from '@/lib/imageOptimization';
import { useSmartImage } from '@/hooks/useSmartImage';

export interface BusinessUnitPill {
  value: string;
  label: string;
}

export interface BusinessUnitCardProps {
  /** Card title */
  title: string;
  /** Card description */
  description: string;
  /** Icon (imported SVG) */
  icon: string;
  /** Array of stat pills */
  pills: BusinessUnitPill[];
  /** Optional background image URL */
  backgroundImage?: string;
  /** Background position (default: "center center") */
  backgroundPosition?: string;
  /** Additional className */
  className?: string;
}

/**
 * component-card-business-unit
 * 
 * A business unit showcase card with icon, description, and stat pills.
 * Supports both solid color and image backgrounds.
 * 
 * Tokens applied:
 * - Dimensions: h-[440px] md:h-[500px]
 * - Surface: bg-grey (#e8e8ed) or background-image
 * - Radius: rounded-2xl
 * - Padding: p-6
 * - Layout: flex flex-col, content top, pills bottom
 * - Title: text-2xl md:text-3xl font-bold (white on image, foreground on solid)
 * - Description: text-sm (white on image, muted-foreground on solid)
 * - Icon: w-8 h-8 (inverted on image backgrounds)
 * - Pills: bg-white text-foreground, value font-semibold
 */
export function BusinessUnitCard({
  title,
  description,
  icon,
  pills,
  backgroundImage,
  backgroundPosition = "center center",
  className = "",
}: BusinessUnitCardProps) {
  const hasImage = !!backgroundImage;
  const { ref, inView } = useSmartImage();
  
  return (
    <div
      ref={ref}
      className={cn(
        "relative overflow-hidden rounded-3xl flex flex-col h-[440px] md:h-[500px] px-[35px] py-[35px]",
        className
      )}
      style={hasImage ? {
        backgroundImage: `url(${optimizeImage(backgroundImage, { width: 800, quality: 80 })})`,
        backgroundSize: 'cover',
        backgroundPosition,
      } : {
        backgroundColor: '#e8e8ed' // atom-surface-card-contrast fallback
      }}
    >
      {/* molecule-card-image-top-gradient: Top-down gradient matching GalleryCard */}
      {hasImage && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 via-[40%] to-transparent" />
      )}

      {/* Content - Top */}
      <div className="relative z-10">
        {/* Title and Icon Row */}
        <div className="flex items-start justify-between gap-4 mb-2">
          {/* atom-text-card-title-light: text-32 font-bold text-platinum */}
          <h3 className={cn(
            "text-32 font-bold leading-tight",
            hasImage ? 'text-platinum' : 'text-foreground'
          )}>
            {title}
          </h3>
          {inView ? (
            <img 
              src={icon} 
              alt={title || "Business unit icon"} 
              loading="lazy"
              decoding="async"
              width={32}
              height={32}
              className={cn(
              "w-8 h-8 flex-shrink-0",
                hasImage && 'brightness-0 invert'
              )}
            />
          ) : null}
        </div>

        {/* atom-text-card-body-light: text-17 text-platinum */}
        <p className={cn(
          "text-17 leading-relaxed",
          hasImage ? 'text-platinum' : 'text-muted-foreground'
        )}>
          {description}
        </p>
      </div>

      {/* molecule-tag-group-stats: Pills - Bottom using Tag dark variant */}
      <div className="relative z-10 flex flex-wrap-reverse gap-2 mt-auto">
        {pills.map((pill, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold bg-platinum text-carbon"
          >
            <span className="font-semibold">{pill.value}</span>
            <span>{pill.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default BusinessUnitCard;
